// /forgot-password — request password reset email.
//
// Restyled to match /sign-in's hero+card layout. Form logic untouched:
// POST /v1/auth/forgot-password { email } → backend always returns 200
// (timing-safe: doesn't reveal whether the email exists). UI shows
// "if that email is registered, we sent a link" regardless.

import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForgotPassword } from "@/lib/auth";
import { Icons } from "@/components/icons";
import { AuthCanvas, HeroPane } from "./sign-up";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const forgot = useForgotPassword();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await forgot.mutateAsync(email.trim().toLowerCase());
      setSent(true);
    } catch {
      // Backend should return 200 regardless to avoid email enumeration.
      // If we DO get an error here it's a real outage; still show the
      // "check your email" copy to keep timing-safe.
      setSent(true);
    }
  }

  return (
    <AuthCanvas>
      <HeroPane tr={tr} />

      <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
        {sent ? (
          <>
            <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Bağlantı yolda" : "Link on the way"}</div>
            <h2 style={{ fontSize: 24, marginBottom: 16 }}>{tr ? "E-postanı kontrol et." : "Check your email."}</h2>
            <p style={{ fontSize: 14, color: "var(--tx)", marginBottom: 12, lineHeight: 1.55 }}>
              {tr ? (
                <>
                  <span className="mono" style={{ color: "var(--tx2)" }}>{email}</span>{" "}
                  adresinde bir hesap varsa, parolanı sıfırlama bağlantısını gönderdik.
                </>
              ) : (
                <>
                  If an account exists for{" "}
                  <span className="mono" style={{ color: "var(--tx2)" }}>{email}</span>
                  , we sent a password reset link.
                </>
              )}
            </p>
            <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
              {tr
                ? "Bağlantı 1 saat geçerli. Spam klasörüne de bakmayı unutma."
                : "The link expires in 1 hour. Don't forget to check your spam folder."}
            </p>
            <Link
              to="/sign-in"
              className="btn btn-secondary"
              style={{ width: "100%", justifyContent: "center", padding: "11px 16px" }}
            >
              <Icons.ChevronLeft size={14} />
              {tr ? "Giriş sayfasına dön" : "Back to sign in"}
            </Link>
          </>
        ) : (
          <>
            <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Parola sıfırlama" : "Reset password"}</div>
            <h2 style={{ fontSize: 24, marginBottom: 10 }}>
              {tr ? "Parolanı mı unuttun?" : "Forgot your password?"}
            </h2>
            <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
              {tr
                ? "E-postanı yaz, sana sıfırlama bağlantısı gönderelim."
                : "Enter your email and we'll send you a reset link."}
            </p>

            <form onSubmit={onSubmit}>
              <input
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tr ? "ornek@firma.com" : "you@company.com"}
                style={{ marginBottom: 18 }}
              />

              <button
                type="submit"
                disabled={!email.includes("@") || forgot.isPending}
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "11px 16px", opacity: forgot.isPending ? 0.7 : 1 }}
              >
                {forgot.isPending
                  ? (tr ? "Gönderiliyor…" : "Sending…")
                  : (tr ? "Sıfırlama bağlantısı gönder" : "Send reset link")}
                <Icons.ArrowRight size={14} />
              </button>
            </form>

            <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--mu)" }}>
              <Link to="/sign-in" className="btn btn-ghost btn-sm">
                {tr ? "Giriş yap" : "Sign in"}
              </Link>
              <Link to="/sign-up" className="btn btn-ghost btn-sm">
                {tr ? "Hesap oluştur" : "Create account"}
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthCanvas>
  );
}
